import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';

export interface ShortcutAction {
  key: string;
  description: string;
  handler: () => void;
  scope?: 'global' | 'page';
}

interface KeyboardShortcutsContextType {
  registerShortcut: (shortcut: ShortcutAction) => () => void;
  getActiveShortcuts: () => ShortcutAction[];
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | undefined>(undefined);

export const useKeyboardShortcuts = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcuts must be used within a KeyboardShortcutsProvider');
  }
  return context;
};

// Utility function to parse shortcut strings
const parseShortcut = (shortcutString: string) => {
  const parts = shortcutString.toLowerCase().split('+');

  // 'mod' maps to Meta on Mac, Ctrl on Windows/Linux
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const hasMod = parts.includes('mod');

  return {
    shift: parts.includes('shift'),
    ctrl: parts.includes('ctrl') || (!isMac && hasMod),
    alt: parts.includes('alt'),
    meta: parts.includes('meta') || parts.includes('cmd') || (isMac && hasMod),
    key: parts[parts.length - 1] // Last part is the main key
  };
};

// Check if keyboard event matches shortcut
const matchesShortcut = (event: KeyboardEvent, shortcutString: string): boolean => {
  const shortcut = parseShortcut(shortcutString);
  return (
    event.shiftKey === shortcut.shift &&
    event.ctrlKey === shortcut.ctrl &&
    event.altKey === shortcut.alt &&
    event.metaKey === shortcut.meta &&
    event.key.toLowerCase() === shortcut.key
  );
};

export const KeyboardShortcutsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const shortcutsRef = useRef<Map<string, ShortcutAction>>(new Map());

  const registerShortcut = useCallback((shortcut: ShortcutAction) => {
    const key = `${shortcut.key}_${Date.now()}_${Math.random()}`;
    shortcutsRef.current.set(key, shortcut);

    // Return cleanup function
    return () => {
      shortcutsRef.current.delete(key);
    };
  }, []);

  const getActiveShortcuts = useCallback(() => {
    return Array.from(shortcutsRef.current.values());
  }, []);

  // Global keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when user is typing in inputs, textareas, or contenteditable elements
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('[contenteditable]')
      ) {
        return;
      }

      // Check all registered shortcuts
      const shortcuts = Array.from(shortcutsRef.current.values());
      for (const shortcut of shortcuts) {
        if (matchesShortcut(event, shortcut.key)) {
          event.preventDefault();
          event.stopPropagation();
          shortcut.handler();
          break; // Only trigger the first matching shortcut
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  return (
    <KeyboardShortcutsContext.Provider value={{ registerShortcut, getActiveShortcuts }}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};
